import { TableBuilder } from '../../../../cdl/TableBuilder/TableBuilder';
import { TableBuilderColumn } from '../../../../cdl/TableBuilder/TableBuilderColumn';
import { formatMoney } from '../../../../common/helpers/formatMoney.helper';
import { formatNumber } from '../../../../common/helpers/formatNumber.helper';
import { translate } from '../../../../common/helpers/translate.helper';
import { Characters } from '../../../../common/helpers/unicode.helper';
import { useEnums } from '../../../../common/hooks/useEnums';
import { OfferHistoryProductChange } from '../../history/buildOfferHistoryTransitions';

interface OfferHistoryProductDiffTableProps {
    productChanges: OfferHistoryProductChange[];
}

type OfferHistoryItem = Extract<OfferHistoryProductChange, { kind: 'item' }>['before'];
type OfferHistorySurcharge = Extract<OfferHistoryProductChange, { kind: 'surcharge' }>['before'];
type OfferHistorySamplekit = Extract<OfferHistoryProductChange, { kind: 'samplekit' }>['before'];

interface ProductDiffRow {
    type: string;
    product: string;
    before: string;
    after: string;
}

const getChangeLabel = (changeType: OfferHistoryProductChange['changeType']) => {
    return translate(`offer.history.productChange.${changeType}`);
};

const getProductTypeLabel = (kind: OfferHistoryProductChange['kind']) => {
    switch (kind) {
        case 'item':
            return translate('offer.history.productType.item');
        case 'surcharge':
            return translate('offer.history.productType.surcharge');
        case 'samplekit':
            return translate('offer.history.productType.samplekit');
    }
};

const joinSummaryParts = (parts: Array<string | null>) => {
    const visibleParts = parts.filter(Boolean);

    return visibleParts.length ? visibleParts.join(' | ') : Characters.EN_DASH;
};

export const OfferHistoryProductDiffTable = ({ productChanges }: OfferHistoryProductDiffTableProps) => {
    const { getHumanReadableValue } = useEnums();

    const getItemName = (item?: OfferHistoryItem) => {
        if (!item) {
            return Characters.EN_DASH;
        }

        return item.product?.name ?? item.productId ?? Characters.EN_DASH;
    };

    const getSurchargeName = (surcharge?: OfferHistorySurcharge) => {
        if (!surcharge) {
            return Characters.EN_DASH;
        }

        if (surcharge.name) {
            return surcharge.name;
        }

        return surcharge.surchargeType
            ? getHumanReadableValue('surchargeType', surcharge.surchargeType) ?? surcharge.surchargeType
            : Characters.EN_DASH;
    };

    const getSamplekitName = (samplekit?: OfferHistorySamplekit) => {
        return samplekit?.name ?? translate('order.samplekit.title');
    };

    const getItemSummary = (item?: OfferHistoryItem) => {
        if (!item) {
            return Characters.EN_DASH;
        }

        return joinSummaryParts([
            item.packType ? getHumanReadableValue('packType', item.packType) ?? item.packType : null,
            item.unitSize !== null && item.unitSize !== undefined
                ? formatNumber({ number: item.unitSize, maximumFractionDigits: 3 })
                : null,
            item.units !== null && item.units !== undefined ? formatNumber({ number: item.units }) : null,
            item.unit ? getHumanReadableValue('productUnit', item.unit) ?? item.unit : null,
            item.price
                ? `${translate('offer.history.field.ppl')} ${formatMoney({ value: item.price.value, currency: item.price.currency, maximumFractionDigits: 3 })}`
                : null,
            item.deleted ? translate('offer.history.itemStatus.deleted') : null,
        ]);
    };

    const getSurchargeSummary = (surcharge?: OfferHistorySurcharge) => {
        if (!surcharge) {
            return Characters.EN_DASH;
        }

        return joinSummaryParts([
            surcharge.surchargeType
                ? getHumanReadableValue('surchargeType', surcharge.surchargeType) ?? surcharge.surchargeType
                : null,
            surcharge.value
                ? formatMoney({
                      value: surcharge.value.value,
                      currency: surcharge.value.currency,
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                  })
                : null,

            surcharge.deleted ? translate('offer.history.itemStatus.deleted') : null,
        ]);
    };

    const getSamplekitSummary = (samplekit?: OfferHistorySamplekit) => {
        if (!samplekit) {
            return Characters.EN_DASH;
        }

        return joinSummaryParts([
            samplekit.quantity !== null && samplekit.quantity !== undefined
                ? formatNumber({ number: samplekit.quantity, maximumFractionDigits: 0 })
                : null,
            samplekit.value
                ? formatMoney({
                      value: samplekit.value.value,
                      currency: samplekit.value.currency,
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                  })
                : null,
            samplekit.deleted ? translate('offer.history.itemStatus.deleted') : null,
        ]);
    };

    const rows: ProductDiffRow[] = productChanges.map((change) => {
        let product = Characters.EN_DASH;
        let before = Characters.EN_DASH;
        let after = Characters.EN_DASH;

        switch (change.kind) {
            case 'item':
                product = getItemName(change.after ?? change.before);
                before = getItemSummary(change.before);
                after = getItemSummary(change.after);
                break;
            case 'surcharge':
                product = getSurchargeName(change.after ?? change.before);
                before = getSurchargeSummary(change.before);
                after = getSurchargeSummary(change.after);
                break;
            case 'samplekit':
                product = getSamplekitName(change.after ?? change.before);
                before = getSamplekitSummary(change.before);
                after = getSamplekitSummary(change.after);
                break;
        }

        return {
            type: `${getProductTypeLabel(change.kind)} | ${getChangeLabel(change.changeType)}`,
            product,
            before,
            after,
        };
    });

    return (
        <TableBuilder data={rows}>
            <TableBuilderColumn<ProductDiffRow> header={translate('order.type')} width="14%">
                {(row) => row.type}
            </TableBuilderColumn>
            <TableBuilderColumn<ProductDiffRow> header={translate('order.product')} width="28%">
                {(row) => row.product}
            </TableBuilderColumn>
            <TableBuilderColumn<ProductDiffRow> header={translate('offer.history.compare.before')} width="29%">
                {(row) => row.before}
            </TableBuilderColumn>
            <TableBuilderColumn<ProductDiffRow> header={translate('offer.history.compare.after')} width="29%">
                {(row) => row.after}
            </TableBuilderColumn>
        </TableBuilder>
    );
};
